<template>
    <div>
        <limitless-page-header title="Snittpriser" />
        <div class="content">
            <div class="card">
                <div class="card-header"><h5 class="card-title">Urval</h5></div>
                <div class="card-body">
                    <form @submit.prevent="getReport">
                        <div class="row">
                            <div class="col-md-3 form-group">
                                <label for="average-year">År</label>
                                <select id="average-year" v-model="filters.year" class="form-control" :disabled="busy" @change="clearSubperiod">
                                    <option v-for="year in years" :key="year" :value="String(year)">{{ year }}</option>
                                    <option value="TUTTI">Alla år</option>
                                </select>
                            </div>
                            <div class="col-md-3 form-group">
                                <label for="average-interval">Datumintervall</label>
                                <select id="average-interval" v-model="filters.intervaltype" class="form-control" :disabled="busy" @change="clearSubperiod">
                                    <option value="bokslut">Säsong/bokslutsår (september–augusti)</option>
                                    <option value="kalender">Kalenderår</option>
                                </select>
                            </div>
                            <div class="col-md-2 form-group">
                                <label for="average-view">Vy</label>
                                <select id="average-view" v-model="filters.show" class="form-control" :disabled="busy" @change="viewChanged">
                                    <option value="">Översikt</option>
                                    <option value="orders">Orderlista</option>
                                    <option value="ekonomi">Ekonomi/TB</option>
                                    <option value="produkter">Produktval</option>
                                </select>
                            </div>
                            <div v-if="filters.show" class="col-md-2">
                                <input-with-validation v-model="filters.csid" label="Kortserie" vid="csid" :disabled="busy || Boolean(filters.orderid)" />
                            </div>
                            <div v-if="filters.show === 'ekonomi'" class="col-md-2">
                                <input-with-validation v-model="filters.orderid" label="Kortorder" vid="orderid" :disabled="busy" />
                            </div>
                        </div>
                        <limitless-button type="submit" icon="icon-stats-bars" :busy="busy">Hämta statistik</limitless-button>
                        <router-link v-if="filters.show" :to="reportLink('', '')" class="btn btn-light ml-2">Till översikten</router-link>
                    </form>
                </div>
            </div>
            <div v-if="error_message" class="alert alert-danger" role="alert">{{ error_message }}</div>
            <div v-if="success_message" class="alert alert-success" role="status">{{ success_message }}</div>
            <div v-if="loading" class="text-center mb-3" role="status"><i class="icon-spinner2 spinner mr-2"></i>Hämtar statistik...</div>

            <template v-if="report && !loading">
                <rednax-datatable
                    v-if="report.filters.show !== 'ekonomi'"
                    :data="report.rows"
                    :columns="columns"
                    :sort-by="report.filters.show === 'produkter' ? 'count' : (report.filters.show === 'orders' ? 'order_id' : 'series_id')"
                    :sort-order="report.filters.show === 'produkter' ? 'desc' : 'asc'"
                >
                    <template #header>
                        <div>
                            <h5 class="card-title">{{ report_title }}</h5>
                            <p class="text-muted mb-0">{{ period_label }}. Genererad: {{ report.generated_at }}. Belopp i SEK.</p>
                        </div>
                    </template>
                    <template #cell_series_id="{ row, cell }"><router-link :to="reportLink('orders', row.series_id)">{{ cell }}</router-link></template>
                    <template #cell_name="{ row, cell }">
                        <router-link v-if="report.filters.show === ''" :to="reportLink('orders', row.series_id)">{{ cell }}</router-link>
                        <router-link v-else-if="row.product_id > 0" :to="'/products/' + row.product_id + '/logistics'">{{ cell }}</router-link>
                        <span v-else>{{ cell || 'Saknas' }}</span>
                    </template>
                    <template #cell_order_id="{ cell }"><router-link :to="'/cardorders/' + cell">{{ cell }}</router-link></template>
                    <template #cell_created_at="{ cell }">{{ cell ? cell.substring(0, 10) : 'Saknas' }}</template>
                    <template #cell_company_name="{ row, cell }">{{ cell }}<span v-if="row.end_customer"> / {{ row.end_customer }}</span></template>
                    <template #cell_quantity="{ cell }">{{ formatNumber(cell, 0) }}</template>
                    <template #cell_count="{ cell }">{{ formatNumber(cell, 0) }}</template>
                    <template #cell_average="{ cell }">{{ money(cell) }}</template>
                    <template #cell_unit_price="{ cell }">{{ money(cell) }}</template>
                    <template #cell_value="{ cell }">{{ money(cell) }}</template>
                    <template #cell_actions="{ row }">
                        <router-link :to="reportLink('ekonomi', row.series_id)" class="mr-3">Ekonomi</router-link>
                        <router-link :to="reportLink('produkter', row.series_id)">Produkter</router-link>
                    </template>
                    <template #cell_economy="{ row }"><router-link :to="orderEconomyLink(row.order_id)">Ekonomi</router-link></template>
                    <template #tfoot>
                        <tr>
                            <th :colspan="columns.length" class="text-right">
                                <template v-if="report.filters.show === 'produkter'">Totalt: {{ formatNumber(report.totals.count, 0) }} produktval</template>
                                <template v-else>
                                    Totalt: {{ formatNumber(report.totals.quantity, 0) }} kort för {{ money(report.totals.value) }}
                                    <div v-if="report.filters.show === ''">Övrigt (tvåsiffriga serier): {{ formatNumber(report.totals.other_quantity, 0) }} för {{ money(report.totals.other_value) }}</div>
                                </template>
                            </th>
                        </tr>
                    </template>
                </rednax-datatable>

                <template v-else>
                    <div v-if="report.economy.missing_meta" class="alert alert-warning" role="status">{{ report.economy.missing_meta }} order saknar giltigt TB-underlag och ingår inte i ekonomisummorna.</div>
                    <rednax-datatable :data="economy_rows" :columns="economy_columns">
                        <template #header>
                            <div>
                                <h5 class="card-title">{{ report_title }}</h5>
                                <p class="text-muted mb-0">{{ period_label }}. Ekonomin baseras på sparat TB-underlag. Genererad: {{ report.generated_at }}.</p>
                            </div>
                        </template>
                        <template #cell_value="{ row, cell }">{{ row.unit === 'st' ? formatNumber(cell, 0) + ' st' : money(cell) }}</template>
                        <template #cell_percent="{ cell }">{{ cell == null ? '' : formatNumber(cell, 1) + ' %' }}</template>
                    </rednax-datatable>
                    <div class="mb-3">
                        <button type="button" class="btn btn-light" @click="show_details = !show_details">{{ show_details ? 'Dölj underlag' : 'Visa utökat underlag' }}</button>
                        <limitless-button v-if="report.filters.orderid" class="ml-2" :busy="recalculating" :disabled="loading" @click="recalculateTb">Räkna om TB</limitless-button>
                    </div>
                    <rednax-datatable v-if="show_details" :data="report.economy.orders" :columns="detail_columns" sort-by="order_id">
                        <template #header><h5 class="card-title">TB-underlag per order</h5></template>
                        <template #cell_order_id="{ cell }"><router-link :to="orderEconomyLink(cell)">{{ cell }}</router-link></template>
                        <template #cell_grouped="{ cell }">{{ cell ? 'Snittvärde för serie' : 'Orderdata' }}</template>
                        <template v-for="key in money_fields" v-slot:[`cell_${key}`]="{ cell }">{{ money(cell) }}</template>
                    </rednax-datatable>
                    <rednax-datatable :data="report.statuses" :columns="status_columns" sort-by="status_id">
                        <template #header><h5 class="card-title">Kortstatussummering</h5></template>
                        <template #cell_count="{ cell }">{{ formatNumber(cell, 0) }} st</template>
                        <template #cell_actions="{ row }"><router-link :to="statusLink(row.status_id)">Visa order</router-link></template>
                    </rednax-datatable>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import InputWithValidation from '@components/Form/InputWithValidation';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import apiClient from '@services/ApiClient';
import { formatNumber } from '@services/Helpers';

function defaultFilters() {
    const today = new Date();
    return {
        year: String(today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1),
        intervaltype: 'bokslut',
        show: '',
        id: '',
        csid: '',
        orderid: '',
        week: '',
        month: '',
        toyear: '',
        toweek: '',
        tomonth: '',
    };
}
function sortable(columns) {
    return columns.map((column) => ({ ...column, sortable: !['actions', 'economy'].includes(column.data) }));
}

export default {
    name: 'AveragePrices',
    components: {
        LimitlessPageHeader, InputWithValidation, LimitlessButton, RednaxDatatable,
    },
    data() {
        const today = new Date();
        return {
            filters: defaultFilters(),
            years: Array.from({ length: today.getFullYear() - 2004 }, (_, index) => today.getFullYear() - index),
            report: null,
            loading: false,
            recalculating: false,
            refresh_pending: false,
            page_active: true,
            error_message: '',
            success_message: '',
            show_details: false,
            money_fields: ['cost', 'redeemed_cost', 'revenue', 'tb_now', 'tb_trend'],
            economy_columns: sortable([{ data: 'label', header: 'Nyckeltal' }, { data: 'percent', header: 'Andel', align_right: true }, { data: 'value', header: 'Värde', align_right: true }]),
            status_columns: sortable([{ data: 'status_id', header: 'Status-ID' }, { data: 'name', header: 'Status' }, { data: 'count', header: 'Antal', align_right: true }, { data: 'actions', header: '' }]),
        };
    },
    mounted() { this.readQuery(); },
    beforeDestroy() { this.page_active = false; },
    watch: {
        '$route.query': function queryChanged(query) {
            if (!this.report || !this.queryMatches(query, this.report.filters)) this.readQuery();
        },
    },
    computed: {
        busy() { return this.loading || this.recalculating; },
        report_title() {
            const filters = this.report.filters;
            if (filters.orderid) return 'Ekonomi – kortorder ' + filters.orderid;
            const names = { orders: 'Orderlista', ekonomi: 'Ekonomi/TB', produkter: 'Produktval' };
            return filters.show ? names[filters.show] + ' – kortserie ' + filters.csid : 'Snittpriser';
        },
        period_label() {
            return this.report.range ? this.report.range.start.substring(0, 10) + ' – ' + this.report.range.last_date : 'Alla år';
        },
        columns() {
            const numeric = (data, header) => ({ data, header, align_right: true });
            if (this.report.filters.show === 'orders') {
                return sortable([
                    numeric('order_id', 'Order'), { data: 'created_at', header: 'Datum' }, { data: 'seller_name', header: 'Säljare' },
                    { data: 'company_name', header: 'Kund' }, { data: 'delivery_company', header: 'Leverans' }, numeric('quantity', 'Antal'),
                    numeric('unit_price', 'Pris (SEK)'), numeric('value', 'Summa (SEK)'), { data: 'economy', header: '' },
                ]);
            }
            if (this.report.filters.show === 'produkter') {
                return sortable([numeric('product_id', 'Produkt-ID'), { data: 'name', header: 'Produkt' }, numeric('count', 'Antal produktval')]);
            }
            return sortable([
                numeric('series_id', 'Serie'), { data: 'name', header: 'Benämning' }, numeric('quantity', 'Antal kort'),
                numeric('average', 'Snittpris (SEK)'), numeric('value', 'Totalt (SEK)'), { data: 'actions', header: '' },
            ]);
        },
        economy_rows() {
            const totals = this.report.economy.totals;
            return [
                { label: 'Antal kort', value: totals.cards, unit: 'st' },
                { label: 'Antal aktiverade', value: totals.activated, unit: 'st' },
                {
                    label: 'Antal inlösta', value: totals.redeemed, unit: 'st', percent: totals.redeemed_percent,
                },
                { label: 'Total kostnad', value: totals.cost },
                { label: 'Total kostnad inlösta', value: totals.redeemed_cost },
                { label: 'Snittkostnad inlösta', value: totals.average_redeemed_cost },
                { label: 'Total intäkt', value: totals.revenue },
                { label: 'Intäkt per kort', value: totals.revenue_per_card },
                { label: 'TB just nu', value: totals.tb_now, percent: totals.tb_now_percent },
                { label: 'TB trend (100 % inlösen)', value: totals.tb_trend, percent: totals.tb_trend_percent },
            ];
        },
        detail_columns() {
            return sortable([
                { data: 'order_id', header: 'Order' }, { data: 'cards', header: 'Kort', align_right: true },
                { data: 'redeemed', header: 'Inlösta', align_right: true },
                ...this.money_fields.map((key, index) => ({ data: key, header: ['Kostnad', 'Kostnad inlösta', 'Intäkt', 'TB nu', 'TB trend'][index], align_right: true })),
                { data: 'grouped', header: 'Underlag' }, { data: 'calculated_at', header: 'Beräknat' },
            ]);
        },
    },
    methods: {
        formatNumber,
        money(value) { return value == null ? '–' : formatNumber(value, 2) + ' kr'; },
        queryMatches(query, filters) { return Object.keys(filters).every((key) => (query[key] ?? '') === filters[key]); },
        clearSubperiod() { ['week', 'month', 'toyear', 'toweek', 'tomonth'].forEach((key) => { this.filters[key] = ''; }); },
        viewChanged() {
            this.filters.id = '';
            this.filters.orderid = '';
            if (!this.filters.show) this.filters.csid = '';
        },
        reportLink(show, series) {
            const query = {
                ...(this.report?.filters || this.filters), show, csid: String(series), id: '', orderid: '',
            };
            if (show === 'orders') query.id = String(series);
            return { name: 'stats.average-prices', query: Object.fromEntries(Object.entries(query).filter(([, value]) => value !== '')) };
        },
        orderEconomyLink(id) {
            return { name: 'stats.average-prices', query: { orderid: String(id), show: 'ekonomi', year: 'TUTTI' } };
        },
        statusLink(status) {
            const filters = this.report.filters;
            const query = { advanced: '1', status_id: String(status) };
            if (filters.orderid) query.card_order_id = filters.orderid;
            else query.card_series_id = filters.csid;
            return { path: '/prodorders', query };
        },
        readQuery() {
            if (this.loading) { this.refresh_pending = true; return; }
            this.filters = defaultFilters();
            const query = this.$route.query;
            Object.keys(this.filters).forEach((key) => { if (typeof query[key] === 'string') this.filters[key] = query[key]; });
            if (query.id) { this.filters.csid = query.id.replace(/^P/, ''); this.filters.show = 'orders'; } else if (query.orderid) this.filters.show = 'ekonomi';
            else if (query.csid && !query.show) this.filters.show = 'ekonomi';
            if (!query.year && (query.id || query.csid || query.orderid)) this.filters.year = 'TUTTI';
            if (!query.year && query.intervaltype === 'kalender' && !query.id && !query.csid && !query.orderid) this.filters.year = String(new Date().getFullYear());
            this.getReport();
        },
        async getReport() {
            if (this.loading) return;
            this.loading = true;
            this.error_message = '';
            try {
                // Ett ändrat serienummer ska också gälla äldre länkar med id-parametern.
                const filters = { ...this.filters };
                if (filters.show === 'orders' && filters.id) filters.id = (filters.id.startsWith('P') ? 'P' : '') + filters.csid;
                const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ''));
                const response = await apiClient.get('/vue/stats/average-prices', { params });
                if (!this.page_active || this.refresh_pending) return;
                this.report = response.data;
                this.filters = { ...response.data.filters };
                if (!this.queryMatches(this.$route.query, this.filters)) {
                    const query = Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value !== ''));
                    await this.$router.replace({ name: 'stats.average-prices', query });
                }
            } catch (error) {
                this.report = null;
                this.error_message = error.response?.status === 422 && error.response.data?.errors
                    ? Object.values(error.response.data.errors).flat().join(' ') : 'Statistiken kunde inte hämtas. Försök igen.';
            } finally {
                this.loading = false;
                if (this.page_active && this.refresh_pending) { this.refresh_pending = false; this.readQuery(); }
            }
        },
        async recalculateTb() {
            if (this.busy || !this.report?.filters.orderid) return;
            const orderid = this.report.filters.orderid;
            this.recalculating = true;
            this.error_message = '';
            this.success_message = '';
            try {
                await apiClient.post('/vue/stats/average-prices/recalculate', { orderid });
                if (!this.page_active) return;
                this.success_message = 'TB för kortorder ' + orderid + ' har räknats om.';
                await this.getReport();
            } catch (error) {
                this.error_message = 'TB kunde inte räknas om. Försök igen.';
            } finally { this.recalculating = false; }
        },
    },
};
</script>
